import type { MemberAddress } from "@/lib/queries/member-data";
import { PHONE_ERROR_MESSAGE, PHONE_REGEX } from "@/lib/validations";
import { useForm } from "@tanstack/react-form";
import { Input, Label, RadioGroup, RadioGroupItem } from "@repo/ui";
import { FormControl, FormItem, FormLabel, FormMessage } from "@repo/ui/form";
import { AlertCircle, User } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";

/** Check whether a selected address + email has all required contact fields. */
export function isContactInfoComplete(
  address: MemberAddress | null,
  email: string | null,
): boolean {
  if (!address) return false;
  return !!(address.firstName && address.lastName && email && address.phone);
}

export interface ContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface ContactInfoSectionProps {
  mode: "same" | "different";
  onModeChange: (mode: "same" | "different") => void;
  contactInfo: ContactInfo;
  onContactInfoChange: (info: ContactInfo) => void;
  memberAddress: MemberAddress | null;
  memberEmail: string | null;
  /** When true, hide the "Same as shipping address" option and force "different" mode. */
  disableSameOption?: boolean;
}

const contactInfoSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required"),
  lastName: z.string().trim().min(1, "Last name is required"),
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  phone: z
    .string()
    .trim()
    .min(1, "Phone number is required")
    .regex(PHONE_REGEX, PHONE_ERROR_MESSAGE),
});

/** Extract a Zod error message from a TanStack Form field error entry. */
function getErrorMessage(
  error: string | { message: string } | undefined,
): string {
  if (!error) return "";
  if (typeof error === "string") return error;
  if (typeof error === "object" && "message" in error) return error.message;
  return "";
}

export function ContactInfoSection({
  mode,
  onModeChange,
  contactInfo,
  onContactInfoChange,
  memberAddress,
  memberEmail,
  disableSameOption = false,
}: ContactInfoSectionProps) {
  const form = useForm({
    defaultValues: contactInfo,
    validators: {
      onBlur: contactInfoSchema,
    },
  });

  // Sync form with parent when contactInfo changes externally
  // (e.g. mode switch pre-populates from shipping address)
  useEffect(() => {
    const current = form.state.values;
    if (
      current.firstName !== contactInfo.firstName ||
      current.lastName !== contactInfo.lastName ||
      current.email !== contactInfo.email ||
      current.phone !== contactInfo.phone
    ) {
      form.reset(contactInfo);
    }
  }, [
    form,
    contactInfo.firstName,
    contactInfo.lastName,
    contactInfo.email,
    contactInfo.phone,
  ]);

  // Auto-force "different" mode when the "Same" option is disabled (missing contact fields)
  useEffect(() => {
    if (disableSameOption && mode === "same") {
      onModeChange("different");
      // Pre-populate from shipping address
      onContactInfoChange({
        firstName: memberAddress?.firstName || "",
        lastName: memberAddress?.lastName || "",
        email: memberEmail || "",
        phone: memberAddress?.phone || "",
      });
    }
  }, [
    disableSameOption,
    mode,
    onModeChange,
    onContactInfoChange,
    memberAddress,
    memberEmail,
  ]);

  const hasAllContactFields = !!(
    contactInfo.firstName?.trim() &&
    contactInfo.lastName?.trim() &&
    contactInfo.email?.trim() &&
    contactInfo.phone?.trim()
  );

  return (
    <div className="space-y-4 pt-2">
      <div className="flex flex-col gap-1">
        <span className="font-medium text-sm">Contact Information</span>
      </div>

      {disableSameOption && !hasAllContactFields ? (
        <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg">
          <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Please provide the missing contact information below.
          </p>
        </div>
      ) : !disableSameOption ? (
        <RadioGroup
          value={mode}
          data-testid="contact-radio-group"
          onValueChange={(val: "same" | "different") => {
            onModeChange(val);
            // Pre-populate from shipping address when switching to "different"
            if (val === "different") {
              onContactInfoChange({
                firstName: memberAddress?.firstName || "",
                lastName: memberAddress?.lastName || "",
                email: memberEmail || "",
                phone: memberAddress?.phone || "",
              });
            }
          }}
          className="flex flex-col space-y-3"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="same" id="contact-same" />
            <Label
              htmlFor="contact-same"
              className="font-normal cursor-pointer"
            >
              Same as shipping address
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="different" id="contact-different" />
            <Label
              htmlFor="contact-different"
              className="font-normal cursor-pointer"
            >
              Update contact info
            </Label>
          </div>
        </RadioGroup>
      ) : null}

      {mode === "same" ? (
        <div className="border border-border rounded-lg overflow-hidden bg-card mt-3">
          <div className="w-full flex items-center justify-between p-4 border-b border-border bg-muted/20">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-muted-foreground shrink-0" />
              <div>
                <h4 className="font-medium text-sm text-foreground">
                  Contact Information
                </h4>
              </div>
            </div>
          </div>
          <div className="px-4 pb-4 pt-4 text-sm">
            <div className="space-y-1.5 text-muted-foreground">
              {memberAddress && (
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">Name:</span>{" "}
                  {memberAddress.firstName} {memberAddress.lastName}
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">Email:</span>{" "}
                {memberEmail}
              </div>
              {memberAddress?.phone && (
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">Phone:</span>{" "}
                  {memberAddress.phone}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden bg-card mt-3">
          <div className="w-full flex items-center justify-between p-4 border-b border-border bg-muted/20">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-muted-foreground shrink-0" />
              <div>
                <h4 className="font-medium text-sm text-foreground">
                  Contact Information
                </h4>
              </div>
            </div>
          </div>
          <div className="px-4 pb-4 pt-4 space-y-4">
            {/* Name row */}
            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="firstName"
                children={(field) => (
                  <FormItem>
                    <FormLabel error={field.state.meta.errors.length > 0}>
                      First Name
                    </FormLabel>
                    <FormControl error={field.state.meta.errors.length > 0}>
                      <Input
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => {
                          field.handleChange(e.target.value);
                          onContactInfoChange({
                            ...contactInfo,
                            firstName: e.target.value,
                          });
                        }}
                        placeholder="First Name"
                      />
                    </FormControl>
                    <FormMessage>
                      {getErrorMessage(field.state.meta.errors[0])}
                    </FormMessage>
                  </FormItem>
                )}
              />
              <form.Field
                name="lastName"
                children={(field) => (
                  <FormItem>
                    <FormLabel error={field.state.meta.errors.length > 0}>
                      Last Name
                    </FormLabel>
                    <FormControl error={field.state.meta.errors.length > 0}>
                      <Input
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => {
                          field.handleChange(e.target.value);
                          onContactInfoChange({
                            ...contactInfo,
                            lastName: e.target.value,
                          });
                        }}
                        placeholder="Last Name"
                      />
                    </FormControl>
                    <FormMessage>
                      {getErrorMessage(field.state.meta.errors[0])}
                    </FormMessage>
                  </FormItem>
                )}
              />
            </div>

            {/* Email */}
            <form.Field
              name="email"
              children={(field) => (
                <FormItem>
                  <FormLabel error={field.state.meta.errors.length > 0}>
                    Email
                  </FormLabel>
                  <FormControl error={field.state.meta.errors.length > 0}>
                    <Input
                      type="email"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        field.handleChange(e.target.value);
                        onContactInfoChange({
                          ...contactInfo,
                          email: e.target.value,
                        });
                      }}
                      placeholder="Email Address"
                    />
                  </FormControl>
                  <FormMessage>
                    {getErrorMessage(field.state.meta.errors[0])}
                  </FormMessage>
                </FormItem>
              )}
            />

            {/* Phone */}
            <form.Field
              name="phone"
              children={(field) => (
                <FormItem>
                  <FormLabel error={field.state.meta.errors.length > 0}>
                    Phone
                  </FormLabel>
                  <FormControl error={field.state.meta.errors.length > 0}>
                    <Input
                      type="tel"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        field.handleChange(e.target.value);
                        onContactInfoChange({
                          ...contactInfo,
                          phone: e.target.value,
                        });
                      }}
                      placeholder="Phone Number"
                    />
                  </FormControl>
                  <FormMessage>
                    {getErrorMessage(field.state.meta.errors[0])}
                  </FormMessage>
                </FormItem>
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}
